/**
 * Shared formatting for Story Mode AI summaries. Used by the left-panel
 * Story tab and the fullscreen map HUD so both views render the same text
 * the same way (sections, bullets, humanized timestamps).
 */

export type AiLine = { kind: 'p' | 'li'; text: string };
export type AiBlock = { heading: string | null; lines: AiLine[] };

const BARE_HEADING = /^(executive summary|key findings|actionable conclusions|summary|conclusions|findings)$/i;

export function humanizeAiText(s: string): string {
  return s
    .replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g, (iso) => {
      const d = new Date(iso);
      return Number.isNaN(d.getTime())
        ? iso
        : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    })
    .replace(/\*\*(.+?)\*\*/g, '$1');
}

export function parseAiSummary(raw: string): AiBlock[] {
  const blocks: AiBlock[] = [];
  let cur: AiBlock = { heading: null, lines: [] };
  const flush = () => {
    if (cur.heading || cur.lines.length) blocks.push(cur);
    cur = { heading: null, lines: [] };
  };
  for (const line of raw.replace(/\r\n/g, '\n').split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed === '---') {
      if (trimmed === '---') flush();
      continue;
    }
    const hashed = trimmed.match(/^#{1,3}\s+(.+)/);
    if (hashed || BARE_HEADING.test(trimmed)) {
      flush();
      cur.heading = humanizeAiText((hashed?.[1] ?? trimmed).replace(/\*+/g, '').trim());
      continue;
    }
    const labeled = trimmed.match(/^\*\*(.+?)\*\*:?\s*(.*)$/);
    if (labeled) {
      const rest = labeled[2] ? `${labeled[1]}: ${labeled[2]}` : labeled[1];
      cur.lines.push({ kind: 'li', text: humanizeAiText(rest) });
      continue;
    }
    const bullet = trimmed.match(/^(?:[-*•]|\d+[.)])\s+(.+)/);
    if (bullet) {
      cur.lines.push({ kind: 'li', text: humanizeAiText(bullet[1]) });
      continue;
    }
    cur.lines.push({ kind: 'p', text: humanizeAiText(trimmed) });
  }
  flush();
  return blocks;
}
